import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Heart, X, Sparkle } from '@phosphor-icons/react';

const TRIGGERS = [
  { id: 'voice_reaction', label: 'a voice reply', detail: 'pays anyone who replies to their scenes with their voice' },
  { id: 'scene',          label: 'witnessing a scene', detail: 'pays anyone who comments on their next encounter' },
  { id: 'brush',          label: 'a brush in the wild', detail: 'pays the next bubble whose brushes theirs' },
];

/**
 * BloomButton — "Send a bloom to @user". Anonymous-by-default,
 * pays helpers automatically when the recipient gets the chosen kind
 * of interaction. The recipient never learns the bloom exists.
 */
const BloomButton = ({ recipientUsername, label = 'Send a Bloom' }) => {
  const [open, setOpen] = useState(false);
  const [total, setTotal] = useState(50);
  const [perPer, setPerPer] = useState(5);
  const [trigger, setTrigger] = useState('voice_reaction');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      await api.post('/blooms', {
        recipient_username: recipientUsername,
        amount_resonance_total: Number(total),
        trigger,
        pay_per: Number(perPer),
      });
      toast.success('your bloom is open · they will never know');
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not send');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="bloom-btn"
        onClick={() => setOpen(true)}
        className="px-4 py-2 rounded-full bg-bx-magenta/15 hover:bg-bx-magenta/25 text-bx-magenta text-xs font-bold uppercase tracking-widest flex items-center gap-1.5">
        <Heart size={12} weight="fill" /> {label}
      </button>
      {open && (
        <div className="fixed inset-0 z-[300] grid place-items-center bg-black/85 backdrop-blur px-4" onClick={() => setOpen(false)}>
          <div className="glass rounded-3xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-bx-magenta">a bloom for</div>
                <div className="font-display text-2xl font-black mt-1">@{recipientUsername}</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/60 p-1 rounded-full hover:bg-white/10"><X size={14} /></button>
            </div>
            <p className="text-sm text-white/65">
              You pay anonymously now. As @{recipientUsername} receives the kind of interaction you choose,
              whoever does it gets paid from your bloom. They'll never know who started it.
            </p>

            <div className="mt-4 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-white/60">pay people who give them...</div>
              {TRIGGERS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`bloom-trigger-${t.id}`}
                  onClick={() => setTrigger(t.id)}
                  className={`w-full text-left p-3 rounded-2xl border transition ${
                    trigger === t.id ? 'border-bx-magenta/60 bg-bx-magenta/10' : 'border-white/10 glass-hi hover:bg-white/5'
                  }`}>
                  <div className="font-bold text-sm">{t.label}</div>
                  <div className="text-[11px] text-white/55">{t.detail}</div>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">total bloom</div>
                <input type="number" min={5} max={5000} value={total}
                  onChange={(e) => setTotal(e.target.value)}
                  data-testid="bloom-total"
                  className="w-full glass-hi rounded-xl p-2 text-sm outline-none" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">pay per helper</div>
                <input type="number" min={1} max={200} value={perPer}
                  onChange={(e) => setPerPer(e.target.value)}
                  data-testid="bloom-per"
                  className="w-full glass-hi rounded-xl p-2 text-sm outline-none" />
              </div>
            </div>

            <button onClick={send} disabled={busy}
              data-testid="bloom-send"
              className="mt-4 w-full btn-volt px-4 py-3 rounded-full text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
              <Sparkle size={14} weight="fill" /> {busy ? 'opening…' : `open the bloom · ${total} ✦`}
            </button>
            <div className="text-[10px] text-white/60 mt-2 text-center italic">
              they never see the bloom exists. only the help they receive.
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BloomButton;
